import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'

const resolveDefaultApiV1Url = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return new URL('/api/trendify/v1', window.location.origin).toString()
  }

  return 'http://127.0.0.1:8000/api/trendify/v1'
}

const resolveApiBaseUrl = (): string => {
  const apiV1Url = import.meta.env.VITE_API_URL ?? resolveDefaultApiV1Url()
  const normalizedApiV1Url = apiV1Url.replace(/\/+$/, '')

  if (normalizedApiV1Url.toLowerCase().endsWith('/v1')) {
    return normalizedApiV1Url.slice(0, -3)
  }

  return normalizedApiV1Url
}

const API_BASE_URL = resolveApiBaseUrl()
const SELLER_NOTICE_KEY = 'seller_ui_notice'
const SELLER_NOTICE_EVENT = 'seller-ui-notice'
const DEVICE_ID_STORAGE_KEY = 'trendify-device-id'
const DEVICE_TOKEN_STORAGE_KEYS = [
  'trendify-seller-device-token',
  'trendify-device-token',
  'device_token',
  'fcm_token',
  'push_token',
]

type SellerNoticeType = 'info' | 'success' | 'warning' | 'error'
type SellerNoticeContext = 'activation' | 'upgrade' | 'permission'

export interface SellerSessionRole {
  id: number
  name: string
  display_name: string
}

export interface SellerSessionStaff {
  id: number
  name: string
  email: string
  phone?: string | null
  status: string
  roles: SellerSessionRole[]
}

export interface SellerSession {
  is_staff_session: boolean
  permissions: string[]
  staff?: SellerSessionStaff | null
}

export interface SellerUiNotice {
  type: SellerNoticeType
  title: string
  description: string
  context?: SellerNoticeContext
  requiredFeature?: string
  requiredFeatureLabel?: string
  currentPlanName?: string
  currentPlanFeatures?: string[]
  requiredPermission?: string
  requiredPermissionLabel?: string
}

export interface SellerRegistrationPayload {
  business_name: string
  business_type: string
  owner_name: string
  owner_number: string
  business_email: string
  business_phone: string
  password: string
  password_confirmation: string
  business_address: string
  business_address1: string
  business_address2?: string
  business_city: string
  business_state: string
  business_postal_code: string
  business_country: string
  gst_number: string
  business_purpose: string
  documents: string[]
  bank_account_number?: string
  bank_ifsc?: string
  bank_name?: string
}

export interface IndiaStateOption {
  name: string
  code: string
}

export interface IndiaLocationOption {
  name: string
  code: string
  district?: string | null
  postal_code?: string | null
  state?: string | null
}

export interface IndiaReverseLocation {
  display_name: string
  latitude?: number | null
  longitude?: number | null
  address?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  district?: string | null
  osm_type?: string | null
  osm_id?: string | null
}

export interface IndiaLocationResponse<T> {
  message?: string
  data: T
}

export interface SellerCategoryOption {
  id: number
  name: string
  slug: string
  description?: string | null
  image_url?: string | null
  image_key?: string | null
  product_count?: number
}

export interface SellerAccessState {
  status: string
  requires_plan_activation: boolean
  in_grace_period: boolean
  buffer_hours_remaining: number
  buffer_expires_at?: string | null
  has_operational_access: boolean
}

export interface SellerSubscription {
  id: number
  plan_code: string
  plan_name: string
  billing_cycle: string
  amount: number
  currency: string
  status: string
  starts_at?: string | null
  ends_at?: string | null
  grace_ends_at?: string | null
}

export interface SellerAuthProfile {
  id: number
  business_name: string
  business_email: string
  approval_status: string
  access_state: SellerAccessState
  current_subscription?: SellerSubscription | null
}

export interface SellerHeatmapPoint {
  latitude: number
  longitude: number
  postal_code?: string | null
  pincode?: string | null
  city?: string | null
  area?: string | null
  order_time?: string | null
  count: number
  total_items?: number
  total_orders?: number
  total_value?: number
  radius_meters?: number
  intensity?: number
}

export interface SellerHeatmapLocationOrder {
  id: number
  order_number?: string | null
  total_amount: number
  items_count?: number | null
  created_at?: string | null
}

export interface SellerHeatmapLocationGroup {
  postal_code?: string | null
  pincode?: string | null
  city?: string | null
  area?: string | null
  label: string
  latitude?: number | null
  longitude?: number | null
  count: number
  total_orders: number
  total_value?: number
  total_items?: number
  first_order_at?: string | null
  last_order_at?: string | null
  orders: SellerHeatmapLocationOrder[]
}

export interface SellerHeatmapResponse {
  message?: string
  data: {
    metric: string
    points: SellerHeatmapPoint[]
    locations?: SellerHeatmapLocationGroup[]
    summary?: {
      total_points: number
      total_orders: number
      total_value?: number
      total_items?: number
    }
  }
  approval_status: string
  access_state: SellerAccessState
  current_subscription?: SellerSubscription | null
}

export interface SharedCurrencyConfig {
  currency: string
  symbol: string
  base_currency: string
  rate_source: string
  usd_inr_rate: number
  manual_usd_inr_rate?: number
  free_shipping_threshold: number
  shipping_flat_charge: number
}

interface SellerProfileResponse {
  seller: SellerAuthProfile | null
  session?: SellerSession
}

interface SellerLoginResponse {
  message: string
  access_token: string
  token_type: string
  seller: SellerAuthProfile
  session?: SellerSession
}

interface SellerStaffOtpRequestResponse {
  message: string
  expires_in_minutes: number
}

interface LaravelPaginator<T = any> {
  data: T[]
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

interface SellerDataResponse<T = any> {
  message?: string
  data: T
}

interface SellerPaginatedResponse<T = any> {
  message?: string
  data: LaravelPaginator<T>
}

interface SellerInventoryListResponse<T = any, Summary = any> extends SellerPaginatedResponse<T> {
  summary?: Summary
}

interface SellerOrdersListResponse<T = any, Summary = any, DateRange = any> {
  message?: string
  data?: LaravelPaginator<T> | T[]
  summary?: Summary
  date_range?: DateRange
}

interface SellerPaymentMethodsResponse {
  methods?: any[]
  data?: any
}

interface SellerPlanRenewResponse {
  message?: string
  data?: unknown
  access_state?: SellerAccessState
}

export interface SellerUserPayload {
  name: string
  email?: string
  phone?: string
  status: 'active' | 'inactive'
  role_ids: number[]
}

export interface SellerHeatmapNearbyOrder {
  id: number
  order_number: string
  latitude: number
  longitude: number
  total_amount: number
  items_count?: number
  created_at?: string | null
}

export interface SellerProductReview {
  id: number
  productId: number
  orderItemId: number
  rating: number
  comment?: string | null
  user?: {
    id: number
    name: string
    displayName?: string
    avatarUrl?: string | null
    initials?: string
    verifiedBuyer?: boolean
  } | null
  createdAt?: string | null
}

export interface SellerProductReviewsResponse {
  message?: string
  data: {
    product: {
      id: number
      name: string
      slug?: string | null
      average_rating: number
      review_count: number
    }
    reviews: SellerProductReview[]
    meta?: {
      current_page: number
      last_page: number
      per_page: number
      total: number
    }
  }
}

interface SellerRequestConfig extends AxiosRequestConfig {
  skipSellerRedirectHandling?: boolean
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
})

const inFlightGetRequests = new Map<string, Promise<AxiosResponse<any>>>()

const stableSerialize = (value: unknown): string => {
  if (value === null || value === undefined) {
    return String(value)
  }

  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(',')}]`
  }

  if (typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableSerialize(entryValue)}`)
      .join(',')}}`
  }

  return JSON.stringify(value)
}

const buildGetRequestKey = (url: string, config?: SellerRequestConfig) => {
  const params = config?.params ? stableSerialize(config.params) : ''
  const skipRedirectHandling = config?.skipSellerRedirectHandling ? 'skip-redirect' : 'default'

  return `${url}?${params}&mode=${skipRedirectHandling}`
}

const getOnce = <T,>(url: string, config?: SellerRequestConfig): Promise<AxiosResponse<T>> => {
  const key = buildGetRequestKey(url, config)
  const existingRequest = inFlightGetRequests.get(key) as Promise<AxiosResponse<T>> | undefined

  if (existingRequest) {
    return existingRequest
  }

  const request = api.get<T>(url, config).finally(() => {
    inFlightGetRequests.delete(key)
  })

  inFlightGetRequests.set(key, request as Promise<AxiosResponse<any>>)

  return request
}

const persistSellerNotice = (notice: SellerUiNotice) => {
  localStorage.setItem(SELLER_NOTICE_KEY, JSON.stringify(notice))
  window.dispatchEvent(new Event(SELLER_NOTICE_EVENT))
}

const parseSellerNotice = (rawNotice: string | null): SellerUiNotice | null => {
  if (!rawNotice) {
    return null
  }

  try {
    return JSON.parse(rawNotice) as SellerUiNotice
  } catch {
    return null
  }
}

export const peekSellerNotice = (): SellerUiNotice | null => parseSellerNotice(localStorage.getItem(SELLER_NOTICE_KEY))

export const consumeSellerNotice = (): SellerUiNotice | null => {
  const nextNotice = peekSellerNotice()
  if (!nextNotice) {
    return null
  }

  localStorage.removeItem(SELLER_NOTICE_KEY)
  return nextNotice
}

export const sellerNoticeEventName = SELLER_NOTICE_EVENT

const redirectIfNeeded = (targetPath: string) => {
  if (window.location.pathname !== targetPath) {
    window.location.replace(targetPath)
    return true
  }

  return false
}

const isPlanAccessError = (status?: number, data?: Record<string, any>) => {
  if (status === 402) {
    return true
  }

  if (status !== 403) {
    return false
  }

  const message = String(data?.message ?? '').toLowerCase()
  return Boolean(data?.required_feature) || message.includes('billing plan') || message.includes('seller feature') || message.includes('renewal')
}

const isPermissionAccessError = (status?: number, data?: Record<string, any>) => {
  if (status !== 403) {
    return false
  }

  return typeof data?.required_permission === 'string' && data.required_permission.trim() !== ''
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('seller_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    const data = (error.response?.data ?? {}) as Record<string, any>
    const config = (error.config ?? {}) as SellerRequestConfig
    const skipSellerRedirectHandling = Boolean(config.skipSellerRedirectHandling)

    if (status === 401) {
      if (!skipSellerRedirectHandling) {
        localStorage.removeItem('seller_token')
        persistSellerNotice({
          type: 'warning',
          title: 'Seller session expired',
          description: 'Please log in again to continue with your seller workspace.',
        })

        if (redirectIfNeeded('/seller/login')) {
          return new Promise(() => {})
        }
      }

      return Promise.reject(error)
    }

    if (isPlanAccessError(status, data) && !skipSellerRedirectHandling) {
      const isUpgradeNotice = status === 403 && Boolean(data?.required_feature)

      persistSellerNotice({
        type: isUpgradeNotice ? 'info' : status === 402 ? 'warning' : 'info',
        title: isUpgradeNotice ? 'Feature not included in your plan' : 'Seller plan activation needed',
        description:
          typeof data?.message === 'string' && data.message.trim()
            ? data.message
            : isUpgradeNotice
              ? 'This feature is not included in your current plan. Upgrade your plan or continue using the features already included in your current plan.'
              : 'Open the plan activation page to activate or renew your seller plan.',
        context: isUpgradeNotice ? 'upgrade' : 'activation',
        requiredFeature: typeof data?.required_feature === 'string' ? data.required_feature : undefined,
        requiredFeatureLabel: typeof data?.required_feature_label === 'string' ? data.required_feature_label : undefined,
        currentPlanName: typeof data?.current_plan_name === 'string' ? data.current_plan_name : undefined,
        currentPlanFeatures: Array.isArray(data?.current_plan_features) ? data.current_plan_features.filter((value): value is string => typeof value === 'string') : undefined,
      })

      return Promise.reject(error)
    }

    if (isPermissionAccessError(status, data) && !skipSellerRedirectHandling) {
      persistSellerNotice({
        type: 'warning',
        title: 'Not permitted',
        description:
          typeof data?.message === 'string' && data.message.trim()
            ? data.message
            : 'You are not permitted to access this seller feature with your current role permissions.',
        context: 'permission',
        requiredPermission: typeof data?.required_permission === 'string' ? data.required_permission : undefined,
        requiredPermissionLabel: typeof data?.required_permission_label === 'string' ? data.required_permission_label : undefined,
      })

      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

const jsonConfig = { headers: { 'Content-Type': 'application/json' } }

const legacyPermissionAliases = (requiredPermission: string): string[] => {
  const aliases: string[] = []

  if (requiredPermission.endsWith('.read')) {
    aliases.push(requiredPermission.replace(/\.read$/, '.view'))
  }

  if (requiredPermission.startsWith('dashboard.')) {
    aliases.push('dashboard.view')
  }

  if (requiredPermission.startsWith('products.')) {
    aliases.push('products.manage')
  }

  if (requiredPermission.startsWith('inventory.')) {
    aliases.push('products.manage')
  }

  return [...new Set(aliases)]
}

export const hasSellerPermission = (permissions: string[] | undefined, requiredPermission: string): boolean => {
  if (!requiredPermission) {
    return true
  }

  if (!permissions || permissions.length === 0) {
    return false
  }

  if (permissions.includes('*') || permissions.includes(requiredPermission)) {
    return true
  }

  if (legacyPermissionAliases(requiredPermission).some((alias) => permissions.includes(alias))) {
    return true
  }

  const [requiredDomain] = requiredPermission.split('.')

  if (!requiredDomain) {
    return false
  }

  if (permissions.includes(`${requiredDomain}.manage`) || permissions.includes(`${requiredDomain}.all`)) {
    return true
  }

  if (requiredDomain === 'inventory' && permissions.includes('products.manage')) {
    return true
  }

  return false
}

const methodOverrideFormData = (formData: FormData, method: 'PUT' | 'PATCH') => {
  formData.append('_method', method)
  return formData
}

const getBrowserStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

const createDeviceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const ensureDeviceId = (): string => {
  const storage = getBrowserStorage()

  if (!storage) {
    return createDeviceId()
  }

  const existing = storage.getItem(DEVICE_ID_STORAGE_KEY)?.trim()

  if (existing) {
    return existing
  }

  const nextDeviceId = createDeviceId()
  storage.setItem(DEVICE_ID_STORAGE_KEY, nextDeviceId)

  return nextDeviceId
}

const ensureDeviceToken = (deviceId: string): string => {
  const storage = getBrowserStorage()

  if (!storage) {
    return `web-${deviceId}`
  }

  for (const key of DEVICE_TOKEN_STORAGE_KEYS) {
    const token = storage.getItem(key)?.trim()

    if (token) {
      return token
    }
  }

  const fallbackToken = `web-${deviceId}`
  storage.setItem(DEVICE_TOKEN_STORAGE_KEYS[0], fallbackToken)

  return fallbackToken
}

const resolveLoginDevicePayload = () => {
  const deviceId = ensureDeviceId()

  return {
    device_id: deviceId,
    device_token: ensureDeviceToken(deviceId),
  }
}

export const sellerAuthAPI = {
  register: (data: SellerRegistrationPayload) => api.post('/v1/seller/auth/register', data, jsonConfig),
  login: (business_email: string, password: string) =>
    api.post<SellerLoginResponse>(
      '/v1/seller/auth/login',
      {
        business_email,
        password,
        ...resolveLoginDevicePayload(),
      },
      jsonConfig,
    ),
  requestStaffOtp: (email: string) =>
    api.post<SellerStaffOtpRequestResponse>('/v1/seller/auth/staff/request-otp', { email }, jsonConfig),
  verifyStaffOtp: (email: string, otp: string) =>
    api.post<SellerLoginResponse>(
      '/v1/seller/auth/staff/verify-otp',
      {
        email,
        otp,
        ...resolveLoginDevicePayload(),
      },
    ),
  logout: () => api.post('/v1/seller/auth/logout'),
  profile: () => getOnce<SellerProfileResponse>('/v1/seller/auth/me', { skipSellerRedirectHandling: true }),
  updateProfile: (data: Partial<Pick<SellerRegistrationPayload, 'owner_name' | 'business_name' | 'business_phone' | 'business_address' | 'business_city' | 'business_state' | 'business_postal_code'>>) =>
    api.patch('/v1/seller/auth/profile', data, jsonConfig),
  changePassword: (data: { current_password: string; new_password: string; new_password_confirmation: string }) =>
    api.post('/v1/seller/auth/change-password', data, jsonConfig),
}

export const indiaLocationAPI = {
  states: () => getOnce<IndiaLocationResponse<IndiaStateOption[]>>('/v1/india-locations/states', { skipSellerRedirectHandling: true }),
  cities: async (params: { state: string; query?: string; limit?: number }) => {
    const response = await getOnce<IndiaLocationResponse<(IndiaLocationOption | string)[]>>('/v1/india-locations/cities', {
      params,
      skipSellerRedirectHandling: true,
    })

    const items = (response.data.data ?? []).map((item) =>
      typeof item === 'string'
        ? ({ name: item, state: params.state, display_name: item } as IndiaLocationOption)
        : item,
    )

    return { ...response, data: { ...response.data, data: items } }
  },
  postalCodes: (params: { state: string; city: string; query?: string; limit?: number }) =>
    getOnce<IndiaLocationResponse<IndiaLocationOption[]>>('/v1/india-locations/postal-codes', {
      params,
      skipSellerRedirectHandling: true,
    }),
  reverse: (params: { latitude: number; longitude: number }) =>
    getOnce<IndiaLocationResponse<IndiaReverseLocation | null>>('/v1/india-locations/reverse', {
      params,
      skipSellerRedirectHandling: true,
    }),
}

export const sellerDashboardAPI = {
  stats: <T = unknown>(params?: { start_date?: string; end_date?: string }) => getOnce<T>('/v1/seller/dashboard', { params }),
}

export const sellerProductsAPI = {
  list: (params?: { search?: string; start_date?: string; end_date?: string }) =>
    getOnce<SellerPaginatedResponse>('/v1/seller/products', { params }),
  categories: () => getOnce<{ data: SellerCategoryOption[] }>('/v1/storefront/categories'),
  get: (id: number) => getOnce<SellerDataResponse>(`/v1/seller/products/${id}`),
  reviews: (id: number, params?: { page?: number; per_page?: number }) =>
    getOnce<SellerProductReviewsResponse>(`/v1/seller/products/${id}/reviews`, { params }),
  create: (data: FormData) => api.post('/v1/seller/products', data),
  update: (id: number, data: FormData) => api.post(`/v1/seller/products/${id}`, methodOverrideFormData(data, 'PUT')),
}

export const sellerOrdersAPI = {
  list: (params?: { status?: string; start_date?: string; end_date?: string }) =>
    getOnce<SellerOrdersListResponse>('/v1/seller/orders', { params }),
  heatmap: (params: { metric?: string; radius_meters?: number; group?: boolean; postal_code?: string; city?: string } = {}) =>
    getOnce<SellerHeatmapResponse>('/v1/seller/orders/heatmap', { params }),
  nearby: (params: { latitude: number; longitude: number; radius_meters?: number }) =>
    getOnce<{ message?: string; data: SellerHeatmapNearbyOrder[] }>('/v1/seller/orders/heatmap/nearby', { params }),
  exportOrdersCsv: (params?: { start_date?: string; end_date?: string; status?: string }) =>
    api.get('/v1/seller/orders/export/csv', { params, responseType: 'blob' }),
  downloadMemo: (id: number) =>
    api.get(`/v1/seller/orders/${id}/memo`, { responseType: 'blob' }),
  lookupByBarcode: (barcode: string) =>
    getOnce<SellerDataResponse>(`/v1/seller/orders/lookup`, { params: { barcode } }),
  get: (id: number) => getOnce<SellerDataResponse>(`/v1/seller/orders/${id}`),
  updateStatus: (id: number, status: string, photos?: File[], labels?: string[]) => {
    if (status === 'packed' && photos && photos.length > 0) {
      const formData = new FormData()
      formData.append('status', status)
      photos.forEach((photo, index) => {
        formData.append(`photos[${index}]`, photo)
        if (labels?.[index]) {
          formData.append(`labels[${index}]`, labels[index])
        }
      })
      return api.patch<SellerDataResponse>(`/v1/seller/orders/${id}/status`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }

    return api.patch<SellerDataResponse>(`/v1/seller/orders/${id}/status`, { status }, jsonConfig)
  },
  scanUpdateStatus: (barcode: string, status: string, photos?: File[], labels?: string[]) => {
    const formData = new FormData()
    formData.append('barcode', barcode)
    formData.append('status', status)

    if (photos) {
      photos.forEach((photo, index) => {
        formData.append(`photos[${index}]`, photo)
        if (labels?.[index]) {
          formData.append(`labels[${index}]`, labels[index])
        }
      })
    }

    return api.post<SellerDataResponse>('/v1/seller/orders/scan-status', formData)
  },
}

export const sellerInventoryAPI = {
  list: (params?: { low_stock?: boolean }) => getOnce<SellerInventoryListResponse>('/v1/seller/inventory', { params }),
  analysis: () => getOnce<SellerDataResponse>('/v1/seller/inventory/analysis'),
  updateStock: (productId: number, variationId: number, stock: number) =>
    api.patch(`/v1/seller/inventory/${productId}`, { variation_id: variationId, stock }, jsonConfig),
}

export const sellerRolesAPI = {
  list: () => getOnce<SellerPaginatedResponse>('/v1/seller/roles'),
  get: (id: number) => getOnce<SellerDataResponse>(`/v1/seller/roles/${id}`),
  permissions: () => getOnce<SellerDataResponse>('/v1/seller/roles/permissions'),
  create: (data: { display_name: string; description?: string; permissions: string[] }) => api.post('/v1/seller/roles', data, jsonConfig),
  update: (id: number, data: { display_name?: string; description?: string; permissions?: string[] }) => api.put(`/v1/seller/roles/${id}`, data, jsonConfig),
  delete: (id: number) => api.delete(`/v1/seller/roles/${id}`),
}

export const sellerUsersAPI = {
  list: () => getOnce<SellerPaginatedResponse>('/v1/seller/users'),
  get: (id: number) => getOnce<SellerDataResponse>(`/v1/seller/users/${id}`),
  create: (data: SellerUserPayload) => api.post('/v1/seller/users', data, jsonConfig),
  update: (id: number, data: SellerUserPayload) => api.put(`/v1/seller/users/${id}`, data, jsonConfig),
  delete: (id: number) => api.delete(`/v1/seller/users/${id}`),
}

export const sellerSupportAPI = {
  options: () => getOnce<SellerDataResponse>('/v1/seller/support/options'),
  tickets: () => getOnce<SellerPaginatedResponse>('/v1/seller/support/tickets'),
  createTicket: (data: { topic_code: string; subject?: string; message: string }) => api.post('/v1/seller/support/tickets', data, jsonConfig),
  getTicket: (id: number) => getOnce<SellerDataResponse>(`/v1/seller/support/tickets/${id}`),
  reply: (id: number, message: string) => api.post(`/v1/seller/support/tickets/${id}/reply`, { message }, jsonConfig),
  closeTicket: (id: number) => api.patch(`/v1/seller/support/tickets/${id}/close`),
}

export const sellerProfileAPI = {
  show: () => getOnce<SellerDataResponse>('/v1/seller/profile'),
  updateBilling: (data: { gst_number?: string; bank_account_number?: string; bank_ifsc?: string; bank_name?: string }) =>
    api.patch('/v1/seller/profile/billing', data, jsonConfig),
}

export const sellerSettingsAPI = {
  billing: () => getOnce<any>('/v1/seller/settings/billing'),
  features: () => getOnce<any>('/v1/seller/settings/features'),
  addBilling: (data: { amount: number; due_date: string; description?: string }) => api.post('/v1/seller/settings/billing', data, jsonConfig),
  renewPlan: (data: {
    plan_code?: string
    billing_cycle?: 'monthly' | 'yearly'
    payment_details?: {
      razorpay_order_id?: string
      razorpay_subscription_id?: string
      razorpay_payment_id: string
      razorpay_signature: string
    }
  }) =>
    api.post<SellerPlanRenewResponse>('/v1/seller/settings/billing/renew', data, jsonConfig),
  updateBillingDueDate: (id: number, due_date: string) => api.patch(`/v1/seller/settings/billing/${id}`, { due_date }, jsonConfig),
  activateAddOn: (data: {
  add_on_code: string
  payment_method_type?: 'card' | 'netbanking'
  payment_details?: {
    razorpay_order_id?: string
    razorpay_payment_id: string
    razorpay_signature: string
  }
}) => api.post('/v1/seller/settings/billing/add-ons/purchase', data, jsonConfig),
  removeAddOn: (add_on_code: string) =>
    api.post('/v1/seller/settings/billing/add-ons/remove', { add_on_code }, jsonConfig),
  paymentMethods: () => getOnce<SellerPaymentMethodsResponse>('/v1/seller/settings/payments'),
  addPaymentMethod: (data: {
    type?: 'card' | 'upi' | 'wallet' | 'netbanking'
    label?: string
    cardholder_name?: string
    card_number?: string
    expiry_month?: number
    expiry_year?: number
    upi_id?: string
    wallet_name?: string
    bank_name?: string
  }) =>
    api.post('/v1/seller/settings/payments', data, jsonConfig),
  setDefaultMethod: (id: number) => api.patch(`/v1/seller/settings/payments/${id}/default`),
  deleteMethod: (id: number) => api.delete(`/v1/seller/settings/payments/${id}`),
}

export interface SellerWalletSummaryItem {
  id: number
  seller_id: number
  currency: string
  available_balance: string | number
  pending_payout_balance: string | number
  total_credited: string | number
  total_withdrawn: string | number
}

export interface SellerWalletTransactionItem {
  id: number
  type: string
  direction: string
  gross_amount: string | number
  platform_fee_amount: string | number
  net_amount: string | number
  balance_after: string | number
  currency: string
  description?: string | null
  created_at?: string | null
}

export interface SellerPayoutRequestItem {
  id: number
  amount: string | number
  currency: string
  status: string
  payout_date?: string | null
  requested_at?: string | null
  reviewed_at?: string | null
  rejection_reason?: string | null
  razorpay_payout_id?: string | null
}

export interface SellerWalletSummaryResponse {
  wallet: SellerWalletSummaryItem
  transactions: SellerWalletTransactionItem[]
  payout_requests: SellerPayoutRequestItem[]
  platform_fee_percentage: number
}

export const sellerWalletAPI = {
  summary: (params?: { limit?: number; payout_limit?: number }) => getOnce<{ message: string; data: SellerWalletSummaryResponse }>('/v1/seller/wallet', { params }),
  transactions: (params?: { per_page?: number; type?: string }) => getOnce('/v1/seller/wallet/transactions', { params }),
  exportTransactionsCsv: (params?: { type?: string }) =>
    api.get('/v1/seller/wallet/transactions/export/csv', { params, responseType: 'blob' }),
  payoutRequests: (params?: { per_page?: number; status?: string }) => getOnce('/v1/seller/wallet/payout-requests', { params }),
  requestPayout: (amount: number) => api.post('/v1/seller/wallet/payout-requests', { amount }, jsonConfig),
}

export const sellerCurrencyAPI = {
  getConfig: () => getOnce<{ data: SharedCurrencyConfig }>('/v1/storefront/currency', { skipSellerRedirectHandling: true }),
}

export default api


